import React from "react";
import { computeGaugeDisplay } from "./gaugeRange";

interface EnergyGaugeProps {
  title: string;
  value: number;
  unit: string;
  /** Known-good operating bound, if any — a soft hint, not a hard clamp; the
   * visible scale grows past it to fit the live value/history. */
  min?: number;
  max?: number;
  /** Recent history used to size the dynamic scale. */
  points?: { t: number; v: number }[];
  /** Timestamp (ms since epoch) of the most recent reading, if known. */
  lastUpdate?: number;
  color: string;
  icon?: React.ReactNode;
  trend?: "up" | "down" | "stable";
  trendValue?: number;
  /** When false, render without the outer card chrome so several gauges can
   * share one card (e.g. a combined "double gauge"). Defaults to true. */
  framed?: boolean;
  /** Optional cumulative total shown as a footer (e.g. energy consumed). */
  total?: {
    value: number;
    unit: string;
    label?: string;
  };
}

const EnergyGauge: React.FC<EnergyGaugeProps> = ({
  title,
  value,
  unit,
  min,
  max,
  points,
  lastUpdate,
  color,
  icon,
  trend,
  framed = true,
  total,
}) => {
  const display = computeGaugeDisplay(value, min, max, points);

  const getTrendIcon = () => {
    if (!trend) return null;

    switch (trend) {
      case "up":
        return (
          <svg
            className="w-3 h-3 text-green-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 10l7-7m0 0l7 7m-7-7v18"
            />
          </svg>
        );
      case "down":
        return (
          <svg
            className="w-3 h-3 text-red-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 14l-7 7m0 0l-7-7m7 7V3"
            />
          </svg>
        );
      case "stable":
        return (
          <svg
            className="w-3 h-3 text-gray-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M20 12H4"
            />
          </svg>
        );
      default:
        return null;
    }
  };

  const content = (
    <>
      <div className="flex items-start justify-between mb-4">
        <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">
          {title}
        </h3>
        <span
          className="flex items-center justify-center w-8 h-8 rounded-full bg-white dark:bg-gray-700 shadow-sm flex-shrink-0"
          style={{ color }}
        >
          {icon ??
            (trend ? (
              getTrendIcon()
            ) : (
              <svg
                className="w-4 h-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 15l4-5 4 3 5-7 5 6"
                />
              </svg>
            ))}
        </span>
      </div>

      <div className="flex items-baseline justify-between mb-2">
        <span className="flex items-baseline space-x-1">
          <span className="text-2xl font-bold" style={{ color }}>
            {value.toFixed(1)}
          </span>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {unit}
          </span>
        </span>
        {lastUpdate !== undefined && (
          <span className="text-[11px] text-gray-400 dark:text-gray-500">
            {new Date(lastUpdate).toLocaleTimeString()}
          </span>
        )}
      </div>

      {/* Linear meter: fill grows rightward from the shared 0 baseline */}
      <div
        className="h-2 rounded-full overflow-hidden"
        style={{ backgroundColor: "var(--border-primary, #e5e7eb)" }}
      >
        <div
          className="h-full rounded-full transition-[width] duration-300 ease-in-out"
          style={{ width: `${display.fillPct}%`, backgroundColor: color }}
        />
      </div>
      <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
        <span>{display.leftLabel}</span>
        <span>{display.rightLabel}</span>
      </div>

      {total && (
        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between gap-2">
          <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
            {total.label ?? "Total"}
          </span>
          <span className="flex items-baseline gap-1 flex-shrink-0">
            <span className="text-lg font-bold text-gray-800 dark:text-gray-100">
              {total.value.toLocaleString(undefined, {
                maximumFractionDigits: 1,
              })}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {total.unit}
            </span>
          </span>
        </div>
      )}
    </>
  );

  if (!framed) return content;
  return (
    <div className="h-full rounded-2xl bg-gray-50 dark:bg-gray-800/60 border border-gray-200/70 dark:border-gray-700 p-5 flex flex-col justify-start">
      {content}
    </div>
  );
};

export default EnergyGauge;
